import { useContext, useEffect, useState } from 'react';
import { ReportPeriodContext } from '../context/report-periods/report-periods';
import { UserDetailsContext } from '../context/user-details/user-details';
import ReportActionService from '../shared/services/report-action/report-action.service';
import { IReportAction } from '../shared/services/report-action/report-action.model';

/**
 * The current user's outstanding report actions for the selected open reporting
 * period, fetched through the same endpoint the Actions panel uses and filtered
 * to their own id. A badge on someone's own top bar means "your work", so
 * unassigned and other people's actions are deliberately out of scope; this also
 * matches what the Actions panel lists when it opens, since it defaults to the
 * same user filter.
 *
 * count is null until a fetch succeeds (no open period, no user, still loading,
 * or an API error), which callers treat as "render nothing".
 */
export const useOutstandingReportActions = (enabled: boolean, limit: number = 3) => {
  const reportPeriodContext = useContext(ReportPeriodContext);
  const reportPeriodId = reportPeriodContext.lastSelectedOpenReportId;
  const userId = useContext(UserDetailsContext).userDetails?.id;

  const [count, setCount] = useState<number | null>(null);
  const [actions, setActions] = useState<IReportAction[]>([]);

  useEffect(() => {
    if (!enabled || !reportPeriodId || !userId) {
      setCount(null);
      setActions([]);
      return;
    }
    new ReportActionService()
      .getReportActionsWithPagination(reportPeriodId, `&userId=${userId}&status=outstanding`, 0, limit)
      .then((response) => {
        setActions(response.rows);
        setCount(response.count);
      })
      .catch(() => {
        setActions([]);
        setCount(null);
      });
  }, [enabled, reportPeriodId, userId, limit]);

  return { count, actions };
};
